/**
 * Honorarios — Lista de clientes (maestro de cobranza).
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Download, FileCheck, Loader2, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DespachoSectionCard } from "@/components/despacho/DespachoUi";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import {
  honorariosApi,
  parseHonorariosError,
} from "@/modules/despacho/despachoHonorariosApi";
import { filterRosterItemsByQuery } from "@/modules/despacho/honorariosRosterSearch";
import {
  convenioRoleLabel,
  isConvenioCoveredMember,
} from "@/modules/despacho/honorariosConvenioGroup";

/** Mismos nombres que las tarjetas de Control de cobranza (salvo Pendiente). */
export const BILLING_CONDITION_LABELS = {
  factura: "Clientes con factura CFDI",
  otros_pagos: "Clientes otros conceptos",
  no_recurrente: "Clientes eventuales",
  convenio: "Convenios (grupos)",
  cortesia: "Clientes con cortesía",
  eventual_act_empresarial: "Eventuales act. empresarial",
  eventual: "Pendiente de clasificar",
};

/** Etiqueta corta para badges en tabla. */
export const BILLING_CONDITION_SHORT = {
  factura: "Factura",
  otros_pagos: "Otros conceptos",
  no_recurrente: "Eventual",
  convenio: "Convenio",
  cortesia: "Cortesía",
  eventual_act_empresarial: "Eventual act. emp.",
  eventual: "Pendiente",
};

/** Condición pendiente: sin CFDI positivo aún. Con CFDI el sync pasa a Factura. */
export const PENDING_CLASSIFICATION_CONDITION = "eventual";

/** Cómo entró la fila al maestro (auditoría). */
export const SOURCE_ORIGIN_LABELS = {
  cfdi_sync: "CFDI emitido",
  client_register: "Ficha Clientes",
  manual: "Manual",
  seed_from_clients: "Alta desde clientes",
};

export const ROSTER_EXPORT_SCOPE_OPTIONS = [
  { value: "all", label: "Todos los clientes" },
  { value: "factura", label: "Facturados" },
  { value: "convenio", label: "Convenios" },
];

const EMPTY_FORM = {
  fiscal_client_id: "",
  rfc: "",
  client_name: "",
  billing_condition: "eventual",
  operations_start_at: "",
  operations_end_at: "",
  monthly_fee: "",
  convenio_group_name: "",
  convenio_group_id: "",
  payer_fiscal_client_id: "",
  notes: "",
};

/** Preferencia de «CFDI desde» (v2: por defecto mes en curso, no enero). */
const SYNC_FROM_STORAGE_KEY = "visor:honorarios_roster_sync_from_v2";
/** Marca que ya se hizo (o se omitió) la carga inicial desde enero. */
const INITIAL_SYNC_DONE_KEY = "visor:honorarios_roster_initial_sync_done";
const MES_OPTS = [
  { value: "01", label: "Ene" },
  { value: "02", label: "Feb" },
  { value: "03", label: "Mar" },
  { value: "04", label: "Abr" },
  { value: "05", label: "May" },
  { value: "06", label: "Jun" },
  { value: "07", label: "Jul" },
  { value: "08", label: "Ago" },
  { value: "09", label: "Sep" },
  { value: "10", label: "Oct" },
  { value: "11", label: "Nov" },
  { value: "12", label: "Dic" },
];

function defaultSyncFrom() {
  const now = new Date();
  return {
    year: String(now.getFullYear()),
    month: String(now.getMonth() + 1).padStart(2, "0"),
  };
}

function yearStartSyncFrom() {
  return { year: String(new Date().getFullYear()), month: "01" };
}

function readInitialSyncDone() {
  try {
    return localStorage.getItem(INITIAL_SYNC_DONE_KEY) === "1";
  } catch {
    return false;
  }
}

function markInitialSyncDone() {
  try {
    localStorage.setItem(INITIAL_SYNC_DONE_KEY, "1");
  } catch {
    // ignore
  }
}

function readStoredSyncFrom() {
  try {
    const raw = localStorage.getItem(SYNC_FROM_STORAGE_KEY);
    if (!raw) return defaultSyncFrom();
    const parsed = JSON.parse(raw);
    const year = String(parsed?.year || "").trim();
    const month = String(parsed?.month || "").padStart(2, "0");
    if (/^\d{4}$/.test(year) && MES_OPTS.some((m) => m.value === month)) {
      return { year, month };
    }
  } catch {
    // ignore
  }
  return defaultSyncFrom();
}

function syncFromToIssueDate({ year, month }) {
  return `${year}-${month}-01`;
}

function newGroupId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `grp-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function money(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "—";
  return v.toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

function downloadBlobResponse(response, fallbackName) {
  const blob = response.data;
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const cd = response.headers?.["content-disposition"] || "";
  const match = /filename="([^"]+)"/i.exec(cd);
  link.download = match?.[1] || fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

export default function HonorariosClientRosterPanel({
  clients = [],
  readOnly = false,
  canViewAmounts = true,
  canExport = false,
}) {
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [includeEnded, setIncludeEnded] = useState(false);
  const [filterCond, setFilterCond] = useState("all");
  const [contractFilter, setContractFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [showGroups, setShowGroups] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingItems, setPendingItems] = useState([]);
  const [convenioMemberIds, setConvenioMemberIds] = useState([]);
  const [convenioPrompt, setConvenioPrompt] = useState(null);
  const [syncFrom, setSyncFrom] = useState(readStoredSyncFrom);
  const [clearCfdiBefore, setClearCfdiBefore] = useState(false);
  const formAnchorRef = useRef(null);

  const syncYearOptions = useMemo(() => {
    const current = new Date().getFullYear();
    const years = [];
    for (let y = current; y >= current - 8; y -= 1) years.push(String(y));
    return years;
  }, []);

  const issueDateFrom = useMemo(() => syncFromToIssueDate(syncFrom), [syncFrom]);

  useEffect(() => {
    try {
      localStorage.setItem(SYNC_FROM_STORAGE_KEY, JSON.stringify(syncFrom));
    } catch {
      // ignore
    }
  }, [syncFrom]);

  useEffect(() => {
    if (!formOpen) return undefined;
    const t = window.setTimeout(() => {
      formAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 50);
    return () => window.clearTimeout(t);
  }, [formOpen, editingId]);

  const load = useCallback(async (overrideFilter) => {
    setLoading(true);
    try {
      const activeFilter = overrideFilter !== undefined ? overrideFilter : filterCond;
      const data = await honorariosApi.listClientRoster({
        includeEnded,
        billingCondition: activeFilter === "all" ? undefined : activeFilter,
      });
      setItems(data.items || []);
      setGroups(data.convenio_groups || []);
      const pending = Number(data.summary?.pending_classification ?? 0);
      setPendingCount(Number.isFinite(pending) ? pending : 0);
      const needPendingList = (
        activeFilter === PENDING_CLASSIFICATION_CONDITION
        || showPending
        || overrideFilter === PENDING_CLASSIFICATION_CONDITION
      );
      if (activeFilter === PENDING_CLASSIFICATION_CONDITION) {
        setPendingItems(data.items || []);
      } else if (needPendingList || showPending) {
        try {
          const pendingData = await honorariosApi.listClientRoster({
            includeEnded,
            billingCondition: PENDING_CLASSIFICATION_CONDITION,
          });
          setPendingItems(pendingData.items || []);
          const pcount = Number(pendingData.summary?.pending_classification ?? pendingData.items?.length ?? 0);
          if (Number.isFinite(pcount)) setPendingCount(pcount);
        } catch {
          setPendingItems([]);
        }
      }
      return data;
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
      return null;
    } finally {
      setLoading(false);
    }
  }, [includeEnded, filterCond, showPending]);

  // Solo listar al entrar / al cambiar filtros. No recalcula CFDI en cada visita.
  useEffect(() => {
    load();
  }, [load]);

  // Carga inicial única: lista vacía → importar desde enero del año en curso.
  useEffect(() => {
    if (readOnly || readInitialSyncDone()) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const probe = await honorariosApi.listClientRoster({ includeEnded: true });
        if (cancelled) return;
        if ((probe.items || []).length > 0) {
          markInitialSyncDone();
          return;
        }
        const jan = yearStartSyncFrom();
        const janFrom = syncFromToIssueDate(jan);
        setBusy(true);
        const synced = await honorariosApi.syncClientRosterAll({
          issueDateFrom: janFrom,
          clearCfdiBefore: false,
        });
        if (cancelled) return;
        markInitialSyncDone();
        if (synced?.warning && !(synced.items || []).length) {
          toast.message(String(synced.warning));
        } else {
          const fromCfdi = Number(synced?.summary?.roster_from_cfdi || synced?.summary?.roster_created || 0);
          const fromClients = Number(synced?.summary?.roster_from_clients || synced?.summary?.seed_created || 0);
          toast.success(
            `Carga inicial desde ${jan.year}-01`
              + (fromCfdi || fromClients
                ? `: ${[fromCfdi && `${fromCfdi} CFDI`, fromClients && `${fromClients} del despacho`].filter(Boolean).join(", ")}.`
                : "."),
          );
          toast.message(
            "En adelante la lista no se recalcula al entrar. Use «Sincronizar lista» con el mes en curso para altas nuevas.",
          );
        }
        await load();
      } catch {
        // Si falla, el usuario puede pulsar Sincronizar a mano (no marcamos done).
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [readOnly, load]);

  const clientOptions = useMemo(() => clients || [], [clients]);

  const convenioMemberOptions = useMemo(() => {
    const byId = new Map();
    clientOptions.forEach((c) => {
      const id = String(c.fiscal_client_id || "");
      if (!id) return;
      byId.set(id, {
        fiscal_client_id: id,
        name: c.name || c.client_name || id,
        rfc: c.rfc || "",
      });
    });
    items.forEach((r) => {
      const id = String(r.fiscal_client_id || "");
      if (!id || byId.has(id)) return;
      byId.set(id, {
        fiscal_client_id: id,
        name: r.client_name || id,
        rfc: r.rfc || "",
      });
    });
    return Array.from(byId.values()).sort((a, b) =>
      String(a.name).localeCompare(String(b.name), "es"),
    );
  }, [clientOptions, items]);

  const displayedItems = useMemo(() => {
    let rows = items;
    if (contractFilter === "sin_firmado") {
      rows = items.filter((r) => !r.tiene_contrato_firmado);
    } else if (contractFilter === "firmado") {
      rows = items.filter((r) => r.tiene_contrato_firmado);
    }
    return filterRosterItemsByQuery(rows, searchQuery);
  }, [items, contractFilter, searchQuery]);

  const openCreate = () => {
    setEditingId("");
    setForm(EMPTY_FORM);
    setConvenioMemberIds([]);
    setFormOpen(true);
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    const fee = Number(row.monthly_fee);
    const gid = row.convenio_group_id || "";
    setForm({
      fiscal_client_id: row.fiscal_client_id || "",
      rfc: row.rfc || "",
      client_name: row.client_name || "",
      billing_condition: row.billing_condition || "eventual",
      operations_start_at: row.operations_start_at || "",
      operations_end_at: row.operations_end_at || "",
      // Vacío si es 0/auto: evita marcar fee_manual al guardar sin cambio
      monthly_fee: Number.isFinite(fee) && fee > 0 ? String(fee) : "",
      convenio_group_name: row.convenio_group_name || "",
      convenio_group_id: gid,
      payer_fiscal_client_id: row.payer_fiscal_client_id || "",
      notes: row.notes || "",
    });
    if (row.billing_condition === "convenio" && gid) {
      const members = items
        .filter((r) => r.convenio_group_id === gid && r.billing_condition === "convenio")
        .map((r) => r.fiscal_client_id)
        .filter(Boolean);
      setConvenioMemberIds(members.length ? members : [row.fiscal_client_id].filter(Boolean));
    } else {
      setConvenioMemberIds(row.fiscal_client_id ? [row.fiscal_client_id] : []);
    }
    setFormOpen(true);
  };

  const onPickClient = (fiscalId) => {
    const c = clientOptions.find((x) => String(x.fiscal_client_id) === String(fiscalId));
    setForm((f) => ({
      ...f,
      fiscal_client_id: fiscalId,
      rfc: c?.rfc || f.rfc,
      client_name: c?.name || f.client_name,
    }));
    setConvenioMemberIds((prev) => (
      prev.includes(fiscalId) ? prev : [...prev, fiscalId]
    ));
  };

  const mergeSelfIntoMembers = (memberIds, selfId) => {
    const base = [...new Set((memberIds || []).filter(Boolean))];
    if (selfId && !base.includes(selfId)) base.push(selfId);
    return base;
  };

  const applyConvenioNewGroup = () => {
    setForm((f) => ({
      ...f,
      billing_condition: "convenio",
      convenio_group_id: newGroupId(),
      convenio_group_name: "",
      payer_fiscal_client_id: f.fiscal_client_id || "",
    }));
    setConvenioMemberIds((prev) => {
      const selfId = form.fiscal_client_id;
      if (selfId) return mergeSelfIntoMembers(prev, selfId);
      return prev.length ? prev : [];
    });
  };

  const applyConvenioExistingGroup = (groupId) => {
    const g = groups.find((x) => x.convenio_group_id === groupId);
    if (!g) {
      toast.error("Seleccione un grupo existente.");
      return false;
    }
    const selfId = form.fiscal_client_id || "";
    const memberIds = mergeSelfIntoMembers(
      (g.members || []).map((m) => m.fiscal_client_id),
      selfId,
    );
    setForm((f) => ({
      ...f,
      billing_condition: "convenio",
      convenio_group_id: g.convenio_group_id || groupId,
      convenio_group_name: g.convenio_group_name || "",
      payer_fiscal_client_id: g.payer_fiscal_client_id || f.payer_fiscal_client_id || selfId || "",
    }));
    setConvenioMemberIds(memberIds);
    return true;
  };

  const onConditionChange = (v) => {
    if (v === "convenio") {
      // Siempre preguntar (aunque aún no haya grupos cargados o la lista esté vacía).
      setConvenioPrompt({
        mode: groups.length > 0 ? "existing" : "new",
        groupId: groups[0]?.convenio_group_id || "",
      });
      return;
    }
    setForm((f) => ({
      ...f,
      billing_condition: v,
      convenio_group_id: "",
      convenio_group_name: "",
      payer_fiscal_client_id: "",
    }));
    setConvenioMemberIds([]);
  };

  const openConvenioPrompt = () => {
    setConvenioPrompt({
      mode: groups.length > 0 ? "existing" : "new",
      groupId: groups[0]?.convenio_group_id || "",
    });
  };

  const confirmConvenioPrompt = () => {
    if (!convenioPrompt) return;
    if (convenioPrompt.mode === "existing") {
      if (!applyConvenioExistingGroup(convenioPrompt.groupId)) return;
    } else {
      applyConvenioNewGroup();
    }
    setConvenioPrompt(null);
  };

  const cancelConvenioPrompt = () => {
    setConvenioPrompt(null);
  };

  const onPickExistingGroup = (groupId) => {
    if (!groupId || groupId === "__new__") {
      setForm((f) => ({
        ...f,
        convenio_group_id: newGroupId(),
        convenio_group_name: "",
        payer_fiscal_client_id: f.fiscal_client_id || "",
      }));
      setConvenioMemberIds(form.fiscal_client_id ? [form.fiscal_client_id] : []);
      return;
    }
    const g = groups.find((x) => x.convenio_group_id === groupId);
    if (!g) return;
    const selfId = form.fiscal_client_id || "";
    const memberIds = mergeSelfIntoMembers(
      (g.members || []).map((m) => m.fiscal_client_id),
      selfId,
    );
    setForm((f) => ({
      ...f,
      convenio_group_id: g.convenio_group_id || groupId,
      convenio_group_name: g.convenio_group_name || "",
      payer_fiscal_client_id: g.payer_fiscal_client_id || f.payer_fiscal_client_id || selfId || "",
    }));
    setConvenioMemberIds(memberIds);
  };

  const toggleConvenioMember = (fiscalId, checked) => {
    setConvenioMemberIds((prev) => {
      let next;
      if (checked) {
        next = prev.includes(fiscalId) ? prev : [...prev, fiscalId];
      } else {
        next = prev.filter((id) => id !== fiscalId);
      }
      setForm((f) => {
        const payer = String(f.payer_fiscal_client_id || "").trim();
        if (checked && !payer) {
          return { ...f, payer_fiscal_client_id: fiscalId };
        }
        if (!checked && payer === fiscalId) {
          return { ...f, payer_fiscal_client_id: next[0] || "" };
        }
        return f;
      });
      return next;
    });
  };

  const setConvenioPayer = (fiscalId) => {
    if (!fiscalId) return;
    setConvenioMemberIds((prev) => (prev.includes(fiscalId) ? prev : [...prev, fiscalId]));
    setForm((f) => ({ ...f, payer_fiscal_client_id: fiscalId }));
  };

  const handleSave = async () => {
    if (readOnly) return;
    const isConvenio = form.billing_condition === "convenio";
    if (isConvenio) {
      if (!String(form.convenio_group_name || "").trim()) {
        toast.error("Indique el nombre del convenio o grupo.");
        return;
      }
      const members = [...new Set(convenioMemberIds.filter(Boolean))];
      if (form.fiscal_client_id && !members.includes(form.fiscal_client_id)) {
        members.push(form.fiscal_client_id);
      }
      if (!members.length) {
        toast.error("Seleccione al menos una empresa del grupo.");
        return;
      }
      const payerId = String(form.payer_fiscal_client_id || "").trim() || members[0];
      if (!members.includes(payerId)) {
        toast.error("Elija a cuál empresa se le factura (debe estar marcada en el grupo).");
        return;
      }
      setBusy(true);
      try {
        const feeRaw = String(form.monthly_fee ?? "").trim();
        const feeNum = feeRaw === "" ? undefined : Number(feeRaw);
        const primary = form.fiscal_client_id || payerId || members[0];
        await honorariosApi.assignConvenioGroup({
          convenio_group_name: form.convenio_group_name.trim(),
          convenio_group_id: form.convenio_group_id || "",
          payer_fiscal_client_id: payerId,
          primary_fiscal_client_id: primary,
          member_fiscal_client_ids: members,
        });
        // Honorario / observaciones del cliente principal (si se capturaron)
        if (primary && (feeNum != null || form.notes || editingId)) {
          const primaryRow = items.find((r) => r.fiscal_client_id === primary);
          const patch = {
            billing_condition: "convenio",
            notes: form.notes,
            convenio_group_id: form.convenio_group_id,
            convenio_group_name: form.convenio_group_name.trim(),
            payer_fiscal_client_id: payerId,
          };
          if (feeNum != null && Number.isFinite(feeNum)) {
            patch.monthly_fee = feeNum;
            patch.fee_manual = feeNum > 0;
          }
          if (primaryRow?.id) {
            await honorariosApi.updateClientRoster(primaryRow.id, patch);
          } else if (form.rfc || form.client_name) {
            await honorariosApi.createClientRoster({
              fiscal_client_id: primary,
              rfc: form.rfc.trim(),
              client_name: form.client_name.trim(),
              ...patch,
            });
          }
        }
        toast.success(`Convenio actualizado (${members.length} empresa(s) en «Convenios (grupos)»).`);
        setFormOpen(false);
        setEditingId("");
        const nextFilter = (
          (showPending || filterCond === PENDING_CLASSIFICATION_CONDITION)
            ? "convenio"
            : filterCond
        );
        if (nextFilter !== filterCond) setFilterCond(nextFilter);
        setShowPending(false);
        await load(nextFilter);
      } catch (err) {
        toast.error(parseHonorariosError(err).message);
      } finally {
        setBusy(false);
      }
      return;
    }

    if (!form.fiscal_client_id) {
      toast.error("Seleccione un cliente.");
      return;
    }
    setBusy(true);
    try {
      const feeRaw = String(form.monthly_fee ?? "").trim();
      const feeNum = feeRaw === "" ? undefined : Number(feeRaw);
      const nextCond = form.billing_condition || PENDING_CLASSIFICATION_CONDITION;
      const body = {
        fiscal_client_id: form.fiscal_client_id,
        rfc: form.rfc.trim(),
        client_name: form.client_name.trim(),
        billing_condition: nextCond,
        operations_start_at: form.operations_start_at,
        operations_end_at: form.operations_end_at || null,
        clear_end: !form.operations_end_at,
        monthly_fee: feeNum,
        // Solo manual si el usuario capturó un monto > 0 (vacío = auto desde CFDI)
        fee_manual: feeNum != null && Number.isFinite(feeNum) && feeNum > 0,
        convenio_group_id: "",
        convenio_group_name: "",
        payer_fiscal_client_id: "",
        notes: form.notes,
      };
      const savedId = editingId;
      if (editingId) {
        await honorariosApi.updateClientRoster(editingId, body);
      } else {
        await honorariosApi.createClientRoster(body);
      }
      toast.success(
        nextCond !== PENDING_CLASSIFICATION_CONDITION
          ? `Guardado como «${BILLING_CONDITION_LABELS[nextCond] || nextCond}».`
          : "Guardado.",
      );
      setFormOpen(false);
      setEditingId("");
      // Quitar de pendientes en UI de inmediato
      if (savedId && nextCond !== PENDING_CLASSIFICATION_CONDITION) {
        setPendingItems((prev) => prev.filter((r) => r.id !== savedId));
        setPendingCount((c) => Math.max(0, Number(c) - 1));
      }
      // Si reclasificó desde Pendientes, mostrar la condición elegida
      const fromPending = showPending || filterCond === PENDING_CLASSIFICATION_CONDITION;
      const nextFilter = (
        fromPending && nextCond !== PENDING_CLASSIFICATION_CONDITION
          ? nextCond
          : filterCond
      );
      if (nextFilter !== filterCond) setFilterCond(nextFilter);
      if (fromPending && nextCond !== PENDING_CLASSIFICATION_CONDITION) {
        setShowPending(false);
      }
      if (nextCond === "factura" && (feeNum == null || feeNum <= 0)) {
        await honorariosApi.refreshClientRosterFees();
      }
      await load(nextFilter);
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleSyncAll = async () => {
    if (readOnly) return;
    if (clearCfdiBefore) {
      const ok = window.confirm(
        `Se eliminarán pendientes sin factura de honorarios desde ${issueDateFrom.slice(0, 7)} (p. ej. última factura en años previos), y las facturas anteriores a ese mes. No se reimportan del catálogo sin CFDI del periodo. Factura/Convenio/Cortesía/Eventuales ya clasificados se conservan. ¿Continuar?`,
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const data = await honorariosApi.syncClientRosterAll({
        issueDateFrom,
        clearCfdiBefore,
      });
      const summary = data.summary || {};
      const fromCfdi = Number(summary.roster_from_cfdi || summary.roster_created || 0);
      const fromClients = Number(summary.roster_from_clients || summary.seed_created || 0);
      const clientsUpdated = Number(summary.roster_clients_updated || summary.seed_updated || 0);
      const fees = Number(summary.fees_updated || 0);
      const cleared = Number(summary.roster_cleared_cfdi || 0);
      const scanned = Number(summary.ingresos_scanned || 0);
      const autoFactura = Number(summary.roster_auto_factura || 0);
      const autoEventuales = Number(summary.roster_auto_eventuales || 0);
      const warning = summary.warning || data.warning;

      if (warning && fromCfdi === 0 && fromClients === 0 && clientsUpdated === 0 && scanned === 0) {
        toast.error(String(warning));
      } else if (fromCfdi + fromClients + clientsUpdated + fees + cleared + autoFactura + autoEventuales > 0) {
        const parts = [];
        if (cleared) parts.push(`${cleared} quitados (CFDI viejos)`);
        if (fromCfdi) parts.push(`${fromCfdi} desde CFDI`);
        if (autoFactura) parts.push(`${autoFactura} → Factura (auto)`);
        if (autoEventuales) parts.push(`${autoEventuales} → Eventuales (auto)`);
        if (fromClients) parts.push(`${fromClients} del despacho`);
        if (clientsUpdated) parts.push(`${clientsUpdated} datos de clientes`);
        if (fees) parts.push(`${fees} honorario(s)`);
        toast.success(`Desde ${issueDateFrom.slice(0, 7)}: ${parts.join("; ")}.`);
      } else if (scanned > 0 || !warning) {
        toast.success(`Lista al día desde ${issueDateFrom.slice(0, 7)}.`);
      } else {
        toast.error(
          String(warning)
            || "No se pudo sincronizar. Verifique el RFC del despacho en Configuración → Perfil.",
        );
      }
      setClearCfdiBefore(false);
      // Reaplicar filtro de condición (el sync regresa el maestro completo)
      const listed = await honorariosApi.listClientRoster({
        includeEnded,
        billingCondition: filterCond === "all" ? undefined : filterCond,
      });
      setItems(listed.items || []);
      setGroups(listed.convenio_groups || []);
      const pending = Number(listed.summary?.pending_classification ?? 0);
      setPendingCount(Number.isFinite(pending) ? pending : 0);
      if (filterCond === PENDING_CLASSIFICATION_CONDITION || showPending) {
        if (filterCond === PENDING_CLASSIFICATION_CONDITION) {
          setPendingItems(listed.items || []);
        } else {
          const pendingData = await honorariosApi.listClientRoster({
            includeEnded,
            billingCondition: PENDING_CLASSIFICATION_CONDITION,
          });
          setPendingItems(pendingData.items || []);
        }
      }
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleExportExcel = async () => {
    if (!canExport) {
      toast.error("Sin permiso para exportar.");
      return;
    }
    setBusy(true);
    try {
      const response = await honorariosApi.exportClientRoster({
        billingCondition: filterCond === "all" ? undefined : filterCond,
        includeEnded,
      });
      const slug = filterCond === "all" ? "todos" : filterCond;
      downloadBlobResponse(response, `honorarios_lista_clientes_${slug}.xlsx`);
      toast.success(
        filterCond === "all"
          ? "Excel descargado: subtotales por condición y total general."
          : "Reporte Excel descargado (según filtro activo).",
      );
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const handleEnd = async (row) => {
    if (readOnly) return;
    if (!window.confirm(`¿Dar de baja a ${row.client_name || row.rfc}?`)) return;
    setBusy(true);
    try {
      await honorariosApi.deleteClientRoster(row.id, {
        operations_end_at: new Date().toISOString().slice(0, 10),
      });
      toast.success("Baja registrada.");
      await load();
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const rosterFormEl = formOpen ? (
          <div
            ref={formAnchorRef}
            className="rounded-sm border border-slate-200 bg-slate-50 p-3 space-y-2"
            data-testid="honorarios-roster-form"
          >
            <div className="grid gap-2 sm:grid-cols-2">
              {!editingId ? (
                <div className="sm:col-span-2">
                  <Label className="text-xs">Cliente</Label>
                  <Select value={form.fiscal_client_id || undefined} onValueChange={onPickClient}>
                    <SelectTrigger className="mt-1 h-8 text-xs rounded-sm">
                      <SelectValue placeholder="Seleccione…" />
                    </SelectTrigger>
                    <SelectContent>
                      {clientOptions.map((c) => (
                        <SelectItem key={c.fiscal_client_id} value={c.fiscal_client_id}>
                          {c.name} {c.rfc ? `· ${c.rfc}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : null}
              <div>
                <Label className="text-xs">RFC</Label>
                <Input className="mt-1 h-8 text-xs font-mono" value={form.rfc} onChange={(e) => setForm((f) => ({ ...f, rfc: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Nombre</Label>
                <Input className="mt-1 h-8 text-xs" value={form.client_name} onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Condición (factura / convenio / cortesía…)</Label>
                <Select value={form.billing_condition} onValueChange={onConditionChange}>
                  <SelectTrigger
                    className="mt-1 h-8 text-xs rounded-sm"
                    data-testid="roster-form-condicion"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(BILLING_CONDITION_LABELS).map(([k, label]) => (
                      <SelectItem key={k} value={k}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Honorarios</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  className="mt-1 h-8 text-xs"
                  value={form.monthly_fee}
                  onChange={(e) => setForm((f) => ({ ...f, monthly_fee: e.target.value }))}
                />
              </div>
              <div>
                <Label className="text-xs">Inicio operaciones / contrato</Label>
                <Input
                  type="date"
                  className="mt-1 h-8 text-xs"
                  value={form.operations_start_at}
                  onChange={(e) => setForm((f) => ({ ...f, operations_start_at: e.target.value }))}
                  data-testid="roster-operations-start"
                  title="Fecha base de operación y predeterminado al generar el contrato de servicios"
                />
                <p className="mt-0.5 text-[10px] text-slate-500 m-0">
                  Fecha base del contrato de servicios (se usa al generar el PDF).
                </p>
              </div>
              <div>
                <Label className="text-xs">Fecha término (baja)</Label>
                <Input type="date" className="mt-1 h-8 text-xs" value={form.operations_end_at} onChange={(e) => setForm((f) => ({ ...f, operations_end_at: e.target.value }))} />
              </div>
              {form.billing_condition === "convenio" ? (
                <div className="sm:col-span-2 space-y-2 rounded-sm border border-slate-200 bg-white p-2" data-testid="honorarios-convenio-group-form">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <p className="m-0 text-[11px] text-slate-600 flex-1 min-w-[12rem]">
                      Marque las empresas e indique con el círculo a cuál se factura;
                      las demás quedan sin cobro y se sincronizan juntas.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-7 text-[10px] rounded-sm shrink-0"
                      onClick={openConvenioPrompt}
                      data-testid="honorarios-convenio-reopen-prompt"
                    >
                      ¿Grupo existente o nuevo?
                    </Button>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <div>
                      <Label className="text-xs">Grupo existente o nuevo</Label>
                      <Select
                        value={
                          groups.some((g) => g.convenio_group_id === form.convenio_group_id)
                            ? form.convenio_group_id
                            : "__new__"
                        }
                        onValueChange={onPickExistingGroup}
                      >
                        <SelectTrigger className="mt-1 h-8 text-xs rounded-sm" data-testid="honorarios-convenio-group-select">
                          <SelectValue placeholder="Nuevo o existente…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__new__">Nuevo grupo</SelectItem>
                          {groups.map((g) => (
                            <SelectItem key={g.convenio_group_id} value={g.convenio_group_id}>
                              {g.convenio_group_name || g.convenio_group_id}
                              {g.members?.length ? ` (${g.members.length})` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Nombre del convenio / grupo</Label>
                      <Input
                        className="mt-1 h-8 text-xs"
                        value={form.convenio_group_name}
                        onChange={(e) => setForm((f) => ({ ...f, convenio_group_name: e.target.value }))}
                        placeholder="Ej. Grupo Hernández"
                        data-testid="honorarios-convenio-group-name"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs">Empresas del grupo</Label>
                    <div
                      className="mt-1 max-h-40 overflow-y-auto rounded-sm border border-slate-200 p-2 space-y-1.5"
                      data-testid="honorarios-convenio-members"
                    >
                      {convenioMemberOptions.length ? convenioMemberOptions.map((c) => {
                        const checked = convenioMemberIds.includes(c.fiscal_client_id);
                        const isPayer = String(form.payer_fiscal_client_id || "") === c.fiscal_client_id;
                        return (
                          <div
                            key={c.fiscal_client_id}
                            className="flex items-start gap-2 text-xs text-slate-700"
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(v) => toggleConvenioMember(c.fiscal_client_id, Boolean(v))}
                              className="mt-0.5"
                              aria-label={`Incluir ${c.name}`}
                            />
                            <label className="flex items-start gap-2 flex-1 cursor-pointer min-w-0">
                              <input
                                type="radio"
                                name="convenio-payer"
                                className="mt-0.5 accent-[#0F2D5C]"
                                checked={isPayer}
                                disabled={!checked && !isPayer}
                                onChange={() => setConvenioPayer(c.fiscal_client_id)}
                                data-testid={`honorarios-convenio-payer-${c.fiscal_client_id}`}
                                aria-label={`Se factura a ${c.name}`}
                              />
                              <span className="min-w-0">
                                <span className="font-medium">{c.name}</span>
                                {c.rfc ? <span className="text-slate-500 font-mono"> · {c.rfc}</span> : null}
                                {isPayer ? (
                                  <span className="ml-1 text-[10px] font-semibold text-emerald-800">
                                    Se factura a este
                                  </span>
                                ) : checked ? (
                                  <span className="ml-1 text-[10px] text-slate-500">Sin cobro</span>
                                ) : null}
                              </span>
                            </label>
                          </div>
                        );
                      }) : (
                        <p className="m-0 text-[11px] text-slate-500">
                          No hay clientes disponibles. Importe del despacho o agregue uno.
                        </p>
                      )}
                    </div>
                    <p className="m-0 mt-1 text-[10px] text-slate-500">
                      {convenioMemberIds.length} seleccionada(s). El círculo marca a quién se factura;
                      las desmarcadas que ya estaban en este grupo pasan a Eventuales Despacho Contable.
                    </p>
                  </div>
                </div>
              ) : null}
              <div className="sm:col-span-2">
                <SpellcheckTextarea
                  label="Observaciones"
                  labelClassName="text-xs"
                  className="mt-1 min-h-[64px] rounded-sm border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 resize-y"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder={
                    form.billing_condition === "cortesia"
                      ? "Ej. Familiar del titular / no se cobra por acuerdo…"
                      : "Notas del cliente (opcional)"
                  }
                  testId="honorarios-roster-observaciones"
                  maxLength={2000}
                  lang="es-MX"
                />
                {form.billing_condition === "cortesia" ? (
                  <p className="m-0 mt-1 text-[10px] text-slate-500">
                    Indique por qué no se cobra (queda en el historial del maestro).
                  </p>
                ) : null}
              </div>
              {editingId && form.fiscal_client_id ? (
                <div
                  className="sm:col-span-2 rounded-sm border border-slate-200 bg-slate-50 p-2 space-y-1"
                  data-testid="roster-contrato-firmado-box"
                >
                  <p className="m-0 text-[11px] font-medium text-[#0F2D5C]">Contrato de servicios</p>
                  {(() => {
                    const row = items.find((r) => r.id === editingId)
                      || pendingItems.find((r) => r.id === editingId);
                    const firmado = Boolean(row?.tiene_contrato_firmado);
                    return firmado ? (
                      <Badge className="rounded-sm bg-emerald-100 text-emerald-900 hover:bg-emerald-100 text-[10px]">
                        <FileCheck className="h-3 w-3 mr-1" />
                        Tiene PDF firmado
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="rounded-sm text-[10px] text-slate-600">
                        Sin contrato firmado
                      </Badge>
                    );
                  })()}
                  <p className="m-0 text-[10px] text-slate-500">
                    Genere, envíe y cargue PDFs firmados (con historial) en la pestaña
                    «Contrato de servicios → Contratos».
                  </p>
                </div>
              ) : null}
            </div>
            <div className="flex gap-2">
              <Button type="button" size="sm" className="h-8 text-xs bg-[#0F2D5C]" disabled={busy} onClick={handleSave}>
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Guardar"}
              </Button>
              <Button type="button" size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setFormOpen(false)}>
                Cancelar
              </Button>
            </div>
          </div>
  ) : null;


  const editingFromPending = Boolean(
    formOpen
    && editingId
    && (
      showPending
      || filterCond === PENDING_CLASSIFICATION_CONDITION
      || pendingItems.some((r) => r.id === editingId)
    ),
  );

  return (
    <div className="space-y-3" data-testid="honorarios-client-roster">
      <DespachoSectionCard title="Lista de clientes">
        <p className="m-0 mb-3 text-[11px] text-slate-500">
          Maestro de cobranza (fuente de verdad para Control y el resto de pestañas).
          La <span className="font-medium text-slate-700">Condición</span> (factura, convenio, cortesía, etc.)
          se define aquí: columna «Condición», badge bajo el nombre, o al editar (lápiz) → campo Condición.
          Esa misma clasificación agrupa las tarjetas de Control y el tipo de contrato PDF. Generar, enviar y archivar PDFs firmados se gestiona en «Contrato de servicios → Contratos».
          Al entrar solo se muestra la lista; no se recalculan CFDI.
          La primera vez (lista vacía) se importa desde enero del año en curso.
          Para altas nuevas, deje «CFDI desde» en el mes actual y pulse «Sincronizar lista».
          Con «CFDI desde» (p. ej. julio), al sincronizar se quitan Pendientes sin factura
          de honorarios desde ese mes (clientes viejos del catálogo como SMF) y no se
          reimportan del catálogo sin CFDI del periodo.
          Marque «Limpiar CFDI anteriores» solo si además quiere borrar facturas de
          honorarios anteriores al mes elegido.
          Las filas Eventuales con origen «CFDI emitido» salen de facturas del despacho a ese RFC
          (no hace falta darlas de alta a mano).
        </p>
        <div className="flex flex-wrap gap-2 mb-3 items-center">
          {!readOnly ? (
            <>
              <Button type="button" size="sm" className="rounded-sm h-8 text-xs bg-[#0F2D5C]" onClick={openCreate}>
                <Plus className="h-3.5 w-3.5 mr-1" /> Agregar
              </Button>
              <div
                className={`flex flex-wrap items-center gap-1.5 rounded-sm border px-2 py-1 ${
                  clearCfdiBefore
                    ? "border-amber-400 bg-amber-50"
                    : "border-slate-200 bg-white"
                }`}
                data-testid="honorarios-roster-sync-from"
              >
                <span className="text-[10px] text-slate-500 whitespace-nowrap">CFDI desde</span>
                <Select
                  value={syncFrom.month}
                  onValueChange={(month) => setSyncFrom((s) => ({ ...s, month }))}
                >
                  <SelectTrigger className="h-7 w-[72px] text-xs rounded-sm" aria-label="Mes desde">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MES_OPTS.map((m) => (
                      <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={syncFrom.year}
                  onValueChange={(year) => setSyncFrom((s) => ({ ...s, year }))}
                >
                  <SelectTrigger className="h-7 w-[84px] text-xs rounded-sm" aria-label="Año desde">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {syncYearOptions.map((y) => (
                      <SelectItem key={y} value={y}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <label className="flex items-center gap-1 text-[10px] text-slate-600 cursor-pointer whitespace-nowrap">
                  <Checkbox
                    checked={clearCfdiBefore}
                    onCheckedChange={(v) => setClearCfdiBefore(Boolean(v))}
                    data-testid="honorarios-roster-clear-cfdi"
                  />
                  Limpiar CFDI anteriores
                </label>
                {clearCfdiBefore ? (
                  <span
                    className="text-[10px] font-medium text-amber-800 whitespace-nowrap"
                    data-testid="honorarios-roster-clear-pending"
                  >
                    Pendiente → pulse Sincronizar
                  </span>
                ) : null}
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className={`rounded-sm h-8 text-xs ${
                  clearCfdiBefore
                    ? "border-amber-500 bg-amber-100 text-amber-950 hover:bg-amber-200"
                    : ""
                }`}
                onClick={handleSyncAll}
                disabled={busy}
                aria-busy={busy}
                data-testid="honorarios-roster-sync-all"
              >
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
                {busy
                  ? (clearCfdiBefore ? "Limpiando y sincronizando…" : "Procesando…")
                  : (clearCfdiBefore
                    ? "Sincronizar y limpiar anteriores"
                    : "Sincronizar lista (CFDI + clientes)")}
              </Button>
            </>
          ) : null}
          {canExport ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="rounded-sm h-8 text-xs"
              onClick={handleExportExcel}
              disabled={busy}
              data-testid="honorarios-roster-export-excel"
              title="Descarga el Excel según el filtro activo (con Todos: subtotal por condición + total general)"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1" />}
              Descargar Excel
            </Button>
          ) : null}
          <div className="relative min-w-[200px] flex-1 max-w-xs">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar por nombre o RFC…"
              className="h-8 pl-8 text-xs rounded-sm"
              aria-label="Buscar por nombre o RFC"
              data-testid="honorarios-roster-search"
            />
          </div>
          <Select
            value={filterCond}
            onValueChange={(v) => {
              setFilterCond(v);
              if (v !== PENDING_CLASSIFICATION_CONDITION) setShowPending(false);
            }}
          >
            <SelectTrigger
              className="h-8 w-[280px] text-xs rounded-sm"
              aria-label="Filtrar por condición"
              data-testid="honorarios-roster-filter-condicion"
            >
              <SelectValue placeholder="Condición" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas las condiciones</SelectItem>
              <SelectItem value={PENDING_CLASSIFICATION_CONDITION}>
                Pendiente de clasificar{pendingCount ? ` (${pendingCount})` : ""}
              </SelectItem>
              {Object.entries(BILLING_CONDITION_LABELS)
                .filter(([k]) => k !== PENDING_CLASSIFICATION_CONDITION)
                .map(([k, label]) => (
                  <SelectItem key={k} value={k}>{label}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Select
            value={contractFilter}
            onValueChange={setContractFilter}
          >
            <SelectTrigger
              className="h-8 w-[200px] text-xs rounded-sm"
              aria-label="Filtrar por contrato"
              data-testid="honorarios-roster-filter-contrato"
            >
              <SelectValue placeholder="Contrato" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todo contrato</SelectItem>
              <SelectItem value="sin_firmado">Sin contrato firmado</SelectItem>
              <SelectItem value="firmado">Con contrato firmado</SelectItem>
            </SelectContent>
          </Select>
          <label className="flex items-center gap-1.5 text-xs text-slate-600">
            <Checkbox checked={includeEnded} onCheckedChange={(v) => setIncludeEnded(Boolean(v))} />
            Incluir bajas
          </label>
          <Button
            type="button"
            size="sm"
            variant={showPending ? "default" : "outline"}
            className={`rounded-sm h-8 text-xs ${showPending ? "bg-[#0F2D5C] hover:bg-[#0a2248]" : ""}`}
            onClick={() => {
              setShowPending((v) => {
                const next = !v;
                if (next) {
                  setShowGroups(false);
                  setFilterCond(PENDING_CLASSIFICATION_CONDITION);
                }
                return next;
              });
            }}
            data-testid="honorarios-roster-pending-toggle"
            title="Pendientes: sin CFDI honorarios o aún no sincronizados"
          >
            Pendientes ({pendingCount})
          </Button>
          <Button
            type="button"
            size="sm"
            variant={showGroups ? "default" : "outline"}
            className={`rounded-sm h-8 text-xs ${showGroups ? "bg-[#0F2D5C]" : ""}`}
            onClick={() => {
              setShowGroups((v) => {
                const next = !v;
                if (next) setShowPending(false);
                return next;
              });
            }}
          >
            Grupos ({groups.length})
          </Button>
        </div>

        {formOpen && (!editingId || editingFromPending) ? (
          <div className="mb-3" data-testid="honorarios-roster-form-top">
            {editingFromPending ? (
              <p className="m-0 mb-2 text-[11px] text-[#0F2D5C] font-medium">
                Clasificar pendiente — elija la condición y pulse Guardar.
              </p>
            ) : null}
            {rosterFormEl}
          </div>
        ) : null}

        {showPending ? (
          <div
            className="mb-3 rounded-sm border border-amber-200 bg-amber-50/60 p-3 space-y-2"
            data-testid="honorarios-roster-pending-panel"
          >
            <p className="m-0 text-xs text-amber-950">
              <strong>Pendientes de clasificar ({pendingCount}).</strong>{" "}
              Al sincronizar, quienes traen CFDI de honorarios pasan solos a «Clientes con factura CFDI».
              Convenio, cortesía y eventuales se asignan a mano cuando aplique.
            </p>
            {pendingItems.length ? (
              <ul className="m-0 pl-4 list-disc text-xs text-slate-800 space-y-1">
                {pendingItems.map((row) => (
                  <li key={row.id} className="flex flex-wrap items-center gap-2">
                    <span>
                      <span className="font-medium">{row.client_name || "—"}</span>
                      {" · "}
                      <span className="font-mono">{row.rfc || "—"}</span>
                    </span>
                    {!readOnly ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-6 px-1.5 text-[10px]"
                        onClick={() => openEdit(row)}
                        data-testid={`roster-pending-edit-${row.id}`}
                      >
                        <Pencil className="h-3 w-3 mr-0.5" />
                        Clasificar
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="m-0 text-xs text-slate-600">No hay clientes pendientes de clasificar.</p>
            )}
          </div>
        ) : null}

        {showGroups ? (
          <div className="mb-3 space-y-2" data-testid="honorarios-convenio-groups">
            <p className="m-0 text-xs text-slate-600">
              Mapa de grupos: nombre del convenio, empresas y a quién se factura.
            </p>
            {groups.length ? groups.map((g) => (
              <div key={g.convenio_group_id} className="rounded-sm border border-slate-200 p-2 text-xs">
                <p className="m-0 font-semibold text-[#0F2D5C]">
                  Grupo: {g.convenio_group_name || "Sin nombre"}
                </p>
                {g.payer_client_name || g.payer_fiscal_client_id ? (
                  <p className="m-0 mt-0.5 text-[10px] text-emerald-800">
                    Se factura a: {g.payer_client_name || g.payer_fiscal_client_id}
                  </p>
                ) : null}
                <ul className="m-0 mt-1 pl-4 list-disc text-slate-700">
                  {(g.members || []).map((m) => {
                    const covered = isConvenioCoveredMember(m);
                    const role = convenioRoleLabel(m);
                    return (
                      <li key={m.id}>
                        {m.client_name} · {m.rfc}
                        {role ? (
                          <span className={covered ? "text-slate-500" : "text-emerald-800 font-medium"}>
                            {" "}— {role}
                          </span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )) : (
              <p className="m-0 text-xs text-slate-500">Sin grupos de convenio.</p>
            )}
          </div>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        ) : (
          <div className="overflow-x-auto rounded-sm border border-slate-200">
            <table className="w-full text-[11px] min-w-[900px]" data-testid="honorarios-roster-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left">
                  <th className="p-2 font-medium">RFC</th>
                  <th className="p-2 font-medium">Nombre</th>
                  <th className="p-2 font-medium" data-testid="roster-col-condicion">Condición</th>
                  <th className="p-2 font-medium">Honorarios</th>
                  <th className="p-2 font-medium">Correo electrónico</th>
                  <th className="p-2 font-medium" title="Inicio operaciones / fecha base del contrato">Inicio</th>
                  <th className="p-2 font-medium">Núm. factura</th>
                  <th className="p-2 font-medium">Teléfono</th>
                  <th className="p-2 font-medium">Representante legal</th>
                  <th className="p-2 font-medium">Origen</th>
                  <th className="p-2 font-medium">Término</th>
                  <th className="p-2 font-medium w-24">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {displayedItems.length ? displayedItems.map((row) => {
                  const originLabel = SOURCE_ORIGIN_LABELS[row.source_origin] || row.source_origin || "—";
                  const invoiceNumber = (row.last_invoice_number || "").trim();
                  const invoiceTitle = [
                    invoiceNumber ? `Factura ${invoiceNumber}` : null,
                    row.last_invoice_uuid ? `UUID ${row.last_invoice_uuid}` : null,
                    row.last_invoice_issue_date || null,
                    row.last_invoice_emitter_rfc ? `emisor ${row.last_invoice_emitter_rfc}` : null,
                  ].filter(Boolean).join(" · ");
                  return (
                  <Fragment key={row.id}>
                  <tr
                    className={`border-b border-slate-100 last:border-0 ${formOpen && editingId === row.id ? "bg-sky-50" : ""}`}
                    data-testid={`roster-row-${row.id}`}
                  >
                    <td className="p-2 font-mono">{row.rfc || "—"}</td>
                    <td className="p-2">
                      <span className="font-medium">{row.client_name || "—"}</span>
                      <Badge
                        className="ml-1 align-middle rounded-sm bg-slate-100 text-slate-800 hover:bg-slate-100 text-[9px] px-1 py-0"
                        data-testid={`roster-condicion-badge-${row.id}`}
                        title={BILLING_CONDITION_LABELS[row.billing_condition] || row.billing_condition}
                      >
                        {BILLING_CONDITION_SHORT[row.billing_condition]
                          || BILLING_CONDITION_LABELS[row.billing_condition]
                          || row.billing_condition
                          || "—"}
                      </Badge>
                      {row.billing_condition === "convenio" && row.convenio_group_name ? (
                        <Badge
                          className="ml-1 align-middle rounded-sm bg-sky-100 text-sky-900 hover:bg-sky-100 text-[9px] px-1 py-0"
                          data-testid={`roster-grupo-badge-${row.id}`}
                          title={
                            isConvenioCoveredMember(row)
                              ? "Cubierto por el pagador del grupo (sin cobro)"
                              : "Pagador del grupo (se factura a este)"
                          }
                        >
                          {row.convenio_group_name}
                          {convenioRoleLabel(row) ? ` · ${convenioRoleLabel(row)}` : ""}
                        </Badge>
                      ) : null}
                      {row.tiene_contrato_firmado ? (
                        <Badge
                          className="ml-1 align-middle rounded-sm bg-emerald-100 text-emerald-900 hover:bg-emerald-100 text-[9px] px-1 py-0"
                          data-testid={`roster-contrato-badge-${row.id}`}
                          title="Contrato de servicios firmado"
                        >
                          <FileCheck className="h-2.5 w-2.5 mr-0.5 inline" />
                          Contrato
                        </Badge>
                      ) : null}
                      {row.notes ? (
                        <span className="block text-[10px] text-slate-500 truncate max-w-[220px]" title={row.notes}>
                          {row.notes}
                        </span>
                      ) : null}
                    </td>
                    <td
                      className="p-2 whitespace-nowrap font-medium text-[#0F2D5C]"
                      data-testid={`roster-condicion-${row.id}`}
                      title={BILLING_CONDITION_LABELS[row.billing_condition] || row.billing_condition}
                    >
                      {BILLING_CONDITION_SHORT[row.billing_condition]
                        || BILLING_CONDITION_LABELS[row.billing_condition]
                        || row.billing_condition
                        || "—"}
                    </td>
                    <td className="p-2 whitespace-nowrap">{canViewAmounts ? money(row.monthly_fee) : "•••"}</td>
                    <td className="p-2 max-w-[180px] truncate" title={row.email || undefined}>
                      {row.email || "—"}
                    </td>
                    <td className="p-2 whitespace-nowrap">{row.operations_start_at || "—"}</td>
                    <td
                      className="p-2 font-mono whitespace-nowrap"
                      title={invoiceTitle || undefined}
                      data-testid={`roster-invoice-number-${row.id}`}
                    >
                      {invoiceNumber || "—"}
                    </td>
                    <td className="p-2 whitespace-nowrap font-mono">{row.telefono || "—"}</td>
                    <td className="p-2 max-w-[160px] truncate" title={row.is_empresa && row.representante_legal ? row.representante_legal : undefined}>
                      {row.is_empresa ? (row.representante_legal || "—") : "—"}
                    </td>
                    <td className="p-2 whitespace-nowrap" data-testid={`roster-origin-${row.id}`}>
                      <span title={row.source_origin || undefined}>{originLabel}</span>
                    </td>
                    <td className="p-2 whitespace-nowrap">{row.operations_end_at || "—"}</td>
                    <td className="p-2">
                      {!readOnly ? (
                        <div className="flex gap-1 flex-wrap">
                          <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => openEdit(row)} aria-label="Modificar">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          {row.active ? (
                            <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-700" onClick={() => handleEnd(row)} aria-label="Baja">
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          ) : null}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                  {formOpen && editingId === row.id && !editingFromPending ? (
                    <tr data-testid={`roster-edit-row-${row.id}`} className="border-b border-slate-200">
                      <td colSpan={12} className="p-2 align-top bg-slate-50">
                        {rosterFormEl}
                      </td>
                    </tr>
                  ) : null}
                  </Fragment>
                  );
                }) : (
                  <tr>
                    <td colSpan={12} className="p-3 text-slate-500">
                      {searchQuery.trim()
                        ? "Sin resultados para esa búsqueda. Pruebe otro nombre o RFC, o limpie el filtro."
                        : "Sin clientes en el maestro (o en este filtro de contrato). Use «Sincronizar lista» o «Agregar», o cambie el filtro."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </DespachoSectionCard>

      <Dialog
        open={Boolean(convenioPrompt)}
        onOpenChange={(open) => {
          if (!open) cancelConvenioPrompt();
        }}
      >
        <DialogContent
          className="rounded-sm max-w-md"
          data-testid="honorarios-convenio-prompt"
        >
          <DialogHeader>
            <DialogTitle className="text-[#0F2D5C]">Integrar a convenio / grupo</DialogTitle>
            <DialogDescription className="text-xs text-slate-600">
              ¿Desea agregar esta empresa a un grupo existente o crear un grupo nuevo?
              {form.client_name || form.rfc ? (
                <span className="block mt-1 font-medium text-slate-800">
                  Empresa: {form.client_name || "—"}
                  {form.rfc ? ` · ${form.rfc}` : ""}
                </span>
              ) : null}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-1">
            <label
              className={`flex items-start gap-2 text-xs ${groups.length ? "cursor-pointer" : "opacity-60"}`}
            >
              <input
                type="radio"
                name="convenio-prompt-mode"
                className="mt-0.5 accent-[#0F2D5C]"
                checked={convenioPrompt?.mode === "existing"}
                disabled={!groups.length}
                onChange={() => setConvenioPrompt((p) => (p ? { ...p, mode: "existing" } : p))}
                data-testid="honorarios-convenio-prompt-existing"
              />
              <span>
                <span className="font-medium text-slate-800">Agregar a un grupo existente</span>
                <span className="block text-[11px] text-slate-500">
                  {groups.length
                    ? "La empresa se suma al convenio elegido; el pagador actual del grupo se conserva."
                    : "Aún no hay convenios/grupos. Cree uno nuevo o integre otras empresas primero."}
                </span>
              </span>
            </label>
            {convenioPrompt?.mode === "existing" && groups.length > 0 ? (
              <div className="pl-5">
                <Label className="text-xs">Grupo</Label>
                <Select
                  value={convenioPrompt.groupId || ""}
                  onValueChange={(id) => setConvenioPrompt((p) => (p ? { ...p, groupId: id } : p))}
                >
                  <SelectTrigger
                    className="mt-1 h-8 text-xs rounded-sm"
                    data-testid="honorarios-convenio-prompt-group"
                  >
                    <SelectValue placeholder="Seleccione el grupo…" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map((g) => (
                      <SelectItem key={g.convenio_group_id} value={g.convenio_group_id}>
                        {g.convenio_group_name || g.convenio_group_id}
                        {g.members?.length ? ` (${g.members.length} empresas)` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            <label className="flex items-start gap-2 text-xs cursor-pointer">
              <input
                type="radio"
                name="convenio-prompt-mode"
                className="mt-0.5 accent-[#0F2D5C]"
                checked={convenioPrompt?.mode === "new"}
                onChange={() => setConvenioPrompt((p) => (p ? { ...p, mode: "new", groupId: "" } : p))}
                data-testid="honorarios-convenio-prompt-new"
              />
              <span>
                <span className="font-medium text-slate-800">Crear un grupo nuevo</span>
                <span className="block text-[11px] text-slate-500">
                  Continuará el formulario: nombre del convenio, empresas y a quién se factura.
                </span>
              </span>
            </label>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={cancelConvenioPrompt}
              data-testid="honorarios-convenio-prompt-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="button"
              size="sm"
              className="rounded-sm bg-[#0F2D5C] hover:bg-[#0a2247]"
              onClick={confirmConvenioPrompt}
              data-testid="honorarios-convenio-prompt-confirm"
            >
              Continuar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
